import type {
	OptimisticFieldSet,
	PriorityOptimisticResponse,
	UserOptimisticResponse,
	StatusOptimisticResponse,
	SingleSelectOptimisticResponse,
	SprintOptimisticResponse,
	NumberOptimisticResponse,
} from './types.tsx';

export const toOptimisticPriority = ({
	issueId,
	fieldId,
	fieldType,
	value: { priority },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: PriorityOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		fieldSetId: fieldType,
		type: fieldId,
		fields: {
			edges: [
				{
					node: {
						__typename: 'JiraPriorityField',
						__isJiraIssueField: 'JiraPriorityField',
						fieldId,
						id: `${issueId}/${fieldId}`,
						type: fieldType,
						name: 'Priority',
						priority,
					},
				},
			],
		},
	},
});

export const toOptimisticUser = ({
	issueId,
	fieldId,
	fieldType,
	value: { user },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: UserOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: fieldId,
		fields: {
			edges: [
				{
					node: {
						__typename: 'JiraSingleSelectUserPickerField',
						__isJiraIssueField: 'JiraSingleSelectUserPickerField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Assignee',
						user: user
							? {
									__typename: 'AtlassianAccountUser',
									accountStatus: 'active',
									id: user.id,
									accountId: user.accountId,
									picture: user.picture,
									name: user.name,
								}
							: null,
					},
				},
			],
		},
	},
});

export const toOptimisticStatus = ({
	issueId,
	fieldId,
	fieldType,
	value: { status },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: StatusOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: fieldId,
		fields: {
			edges: [
				{
					node: {
						__typename: 'JiraStatusField',
						__isJiraIssueField: 'JiraStatusField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						status,
					},
				},
			],
		},
	},
});

export const toOptimisticSingleSelect = ({
	issueId,
	fieldId,
	fieldType,
	value: { option },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: SingleSelectOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: 'Category[Category]',
		fields: {
			edges: [
				{
					node: {
						__typename: 'JiraSingleSelectField',
						__isJiraIssueField: 'JiraSingleSelectField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Category',
						fieldOptions: {
							totalCount: 1,
						},
						fieldOption: option
							? {
									id: option.id,
									optionId: option.optionId,
									value: option.value,
									color: option.color,
								}
							: null,
					},
				},
			],
		},
	},
});

export const toOptimisticSprint = ({
	issueId,
	fieldId,
	fieldType,
	value: { sprint },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: SprintOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: fieldId,
		fields: {
			edges: [
				{
					node: {
						__typename: 'JiraSprintField',
						__isJiraIssueField: 'JiraSprintField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Sprint',
						selectedSprintsConnection: {
							edges: sprint
								? [
										{
											node: {
												id: sprint.id,
												sprintId: sprint.sprintId,
												name: sprint.name,
												state: sprint.state,
												endDate: sprint.endDate,
											},
										},
									]
								: [],
						},
					},
				},
			],
		},
	},
});

export const toOptimisticNumber = ({
	issueId,
	fieldId,
	fieldType,
	value: { value },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: NumberOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: fieldId,
		fields: {
			edges: [
				{
					node: {
						__typename: 'JiraNumberField',
						__isJiraIssueField: 'JiraNumberField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Story point estimate',
						number: value,
					},
				},
			],
		},
	},
});
